import { z } from 'zod'

export const GUEST_UNFINISHED_DRAFT_STORAGE_KEY =
  'buffer:publish:guest-unfinished-draft'

const STORAGE_VERSION = 1

const GuestUnfinishedDraftSchema = z
  .object({
    version: z.literal(STORAGE_VERSION),
    draftId: z.string().refine((draftId) => draftId.trim().length > 0),
  })
  .strict()

type StorageAdapter = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export type GuestUnfinishedDraftStore = Readonly<{
  read: () => string | undefined
  mark: (draftId: string) => boolean
  clear: (draftId?: string) => boolean
}>

export function createGuestUnfinishedDraftStore({
  storage,
}: Readonly<{ storage: StorageAdapter }>): GuestUnfinishedDraftStore {
  const read = (): string | undefined => {
    let serializedMarker: string | null
    try {
      serializedMarker = storage.getItem(GUEST_UNFINISHED_DRAFT_STORAGE_KEY)
    } catch {
      return undefined
    }

    if (serializedMarker === null) return undefined

    let parsedMarker: unknown
    try {
      parsedMarker = JSON.parse(serializedMarker)
    } catch {
      return undefined
    }

    const result = GuestUnfinishedDraftSchema.safeParse(parsedMarker)
    return result.success ? result.data.draftId : undefined
  }

  return {
    read,
    mark: (draftId) => {
      try {
        storage.setItem(
          GUEST_UNFINISHED_DRAFT_STORAGE_KEY,
          JSON.stringify({ version: STORAGE_VERSION, draftId }),
        )
        return true
      } catch {
        return false
      }
    },
    clear: (draftId) => {
      if (draftId !== undefined && read() !== draftId) return false

      try {
        storage.removeItem(GUEST_UNFINISHED_DRAFT_STORAGE_KEY)
        return true
      } catch {
        return false
      }
    },
  }
}

let browserGuestUnfinishedDraftStore: GuestUnfinishedDraftStore | undefined

export function getBrowserGuestUnfinishedDraftStore(): GuestUnfinishedDraftStore {
  if (browserGuestUnfinishedDraftStore) return browserGuestUnfinishedDraftStore

  if (typeof window === 'undefined') {
    throw new Error('The guest unfinished draft store requires a browser.')
  }

  browserGuestUnfinishedDraftStore = createGuestUnfinishedDraftStore({
    storage: {
      getItem: (key) => window.localStorage.getItem(key),
      setItem: (key, value) => window.localStorage.setItem(key, value),
      removeItem: (key) => window.localStorage.removeItem(key),
    },
  })
  return browserGuestUnfinishedDraftStore
}
